// src/store/reducers/reducerHelpers/thunkPut.ts
import { axiosClientNoCache, csrf } from '@lib/axiosClientCached.ts';
import { processStoreError } from '@lib/errorHandler';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectCurrentCompanySlug } from '@store/reducers/meReducer.ts';
import { addErrorResponseNotification } from '@store/reducers/notificationReducer.ts';
import {
  type InterfaceFunctionReplace,
  type InterfaceVarReplace,
  replaceUrlState,
  replaceUrlVariable,
  safeDispatchActions,
} from '@store/reducers/reducerHelpers/thunkHelperShared.ts';

import type { DispatchActionType } from '@/types/DispatchActionType.ts';

export interface InterfaceThunkPutData<T = undefined, U = undefined | InterfaceVarReplace> {
  form?: T;
  urlVarsReplaceMap?: U;
}

const genericTypeThunkPutReplace = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createAsyncThunk(
    `${type}/put/${url}`, // Note the "put" in the type
    async (data: InterfaceThunkPutData<T, U> | undefined, { rejectWithValue, dispatch, getState }) => {
      let fullUrl = replaceUrlState(url, replaceMap, getState);
      if (data?.urlVarsReplaceMap !== undefined) {
        fullUrl = replaceUrlVariable(fullUrl, data.urlVarsReplaceMap ?? {});
      }
      await csrf();
      const res = await axiosClientNoCache.put(fullUrl, data?.form).catch((error) => {
        // Using .put()
        dispatch(addErrorResponseNotification({ error, rejectWithValue: undefined }));
        safeDispatchActions(dispatch, errorDispatchArray);
        const processedError = processStoreError(error);
        throw rejectWithValue({
          error: {
            message: processedError.message,
            statusCode: processedError.statusCode,
            data: processedError.data,
          },
        });
      });
      safeDispatchActions(dispatch, successDispatchArray);
      return res.data.data;
    },
  );

const genericTypeThunkPut = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) => genericTypeThunkPutReplace<T, U>(type, url, {}, successDispatchArray, errorDispatchArray);

const genericTypeThunkPutCompany = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  genericTypeThunkPutReplace<T, U>(
    type,
    url,
    { '{companySlug}': selectCurrentCompanySlug },
    successDispatchArray,
    errorDispatchArray,
  );

export const thunkPut = {
  replUrl: genericTypeThunkPutReplace,
  compUrl: genericTypeThunkPutCompany,
  put: genericTypeThunkPut,
};

export default thunkPut;
